import { isFilterGroup } from '@/types'
import type { FilterGroup, FilterCondition, FilterOperator } from '@/types'
import type { DagColumnMeta, DropdownOptionRead } from '@/types/dag'
import { HIERARCHY_COLUMN_IDS } from './customColumnRegistry'

export const EMPTY_FILTER_GROUP: FilterGroup = { op: 'AND', conditions: [] }

/** Column shapes that drive which operators/value widget a filter row offers. */
export type FieldWidget = 'dropdown' | 'number' | 'boolean' | 'date' | 'text'

export interface FieldMeta {
  label: string
  widget: FieldWidget
  options?: { value: string; label: string }[]
}

const NUMERIC_FORMATS = new Set([
  'currency',
  'currency_3dp',
  'percent',
  'number',
  'integer',
])

/** Aggregate columns are cross-row summaries, not per-row values — not filterable. */
export function isFilterableColumn(column: DagColumnMeta): boolean {
  return column.kind !== 'aggregate'
}

function getFieldWidget(column: DagColumnMeta): FieldWidget {
  if (
    HIERARCHY_COLUMN_IDS.has(column.id) ||
    column.dropdown_category ||
    (column.options && column.options.length > 0)
  ) {
    return 'dropdown'
  }
  if (column.format === 'boolean') return 'boolean'
  if (column.format === 'date') return 'date'
  if (NUMERIC_FORMATS.has(column.format)) return 'number'
  return 'text'
}

export const OPERATORS_BY_WIDGET: Record<
  FieldWidget,
  { value: FilterOperator; label: string }[]
> = {
  dropdown: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'in', label: 'is any of' },
  ],
  number: [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '!=' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '<=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '>=' },
  ],
  boolean: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
  ],
  date: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'lt', label: 'before' },
    { value: 'lte', label: 'on or before' },
    { value: 'gt', label: 'after' },
    { value: 'gte', label: 'on or after' },
  ],
  text: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'contains', label: 'contains' },
    { value: 'starts_with', label: 'starts with' },
  ],
}

/** Default operator per widget — numeric/dropdown/boolean/date default to exact
 * match (`eq`) so a numeric search for "9.99" never falls back to a substring
 * match against "19.99"/"29.99"; free text defaults to `contains` since that's
 * the more useful default for descriptive fields. */
export function defaultOperatorFor(widget: FieldWidget): FilterOperator {
  return widget === 'text' ? 'contains' : 'eq'
}

export function defaultValueFor(operator: FilterOperator): string | string[] {
  return operator === 'in' ? [] : ''
}

/**
 * Build per-field metadata (label, operator/value-widget kind, dropdown
 * options) from DAG column metadata, reusing the same dropdown-options
 * resolution already used for cell editing (buildDagColumnDefs.ts /
 * DagCellRenderer.tsx): dropdown_category -> live lookup, else static options.
 */
export function buildDagFieldMeta(
  columns: DagColumnMeta[],
  dropdownMap: Record<string, DropdownOptionRead[]>,
  hierarchyOptions: Partial<Record<string, DropdownOptionRead[]>> = {}
): Record<string, FieldMeta> {
  const meta: Record<string, FieldMeta> = {}
  for (const column of columns) {
    const widget = getFieldWidget(column)
    let options: { value: string; label: string }[] | undefined
    if (widget === 'dropdown') {
      let raw: DropdownOptionRead[] | undefined
      if (HIERARCHY_COLUMN_IDS.has(column.id)) {
        raw = hierarchyOptions[column.id]
      } else {
        const toOption = (value: string): DropdownOptionRead => ({ value })
        raw = column.dropdown_category
          ? (dropdownMap[column.dropdown_category] ??
            column.options?.map(toOption))
          : column.options?.map(toOption)
      }
      options = (raw ?? []).map(o => ({
        value: o.value,
        label: o.display_label ?? o.value,
      }))
    }
    meta[column.id] = { label: column.label, widget, options }
  }
  return meta
}

/** Recursively counts leaf conditions in a filter tree. */
export function countConditions(group: FilterGroup | null | undefined): number {
  if (!group) return 0
  let count = 0
  for (const item of group.conditions) {
    count += isFilterGroup(item) ? countConditions(item) : 1
  }
  return count
}

function serializeCondition(
  condition: FilterCondition,
  fieldMeta: Record<string, FieldMeta>
): FilterCondition | null {
  const widget = fieldMeta[condition.field]?.widget ?? 'text'

  if (condition.operator === 'in') {
    const values = Array.isArray(condition.value) ? condition.value : []
    return values.length > 0 ? { ...condition, value: values } : null
  }

  const raw = condition.value
  if (typeof raw === 'string' && raw.trim() === '') return null

  if (widget === 'number') {
    const num = Number(raw)
    return Number.isNaN(num) ? null : { ...condition, value: num }
  }
  if (widget === 'boolean') {
    return { ...condition, value: raw === 'true' }
  }
  return condition
}

/**
 * Convert the builder's UI-friendly draft (values kept as strings while
 * editing) into a backend-ready FilterGroup: numeric/boolean values coerced
 * to their real types, and incomplete/empty conditions and emptied nested
 * groups dropped.
 */
export function serializeDagFilterGroup(
  group: FilterGroup,
  fieldMeta: Record<string, FieldMeta>
): FilterGroup {
  const conditions: (FilterCondition | FilterGroup)[] = []
  for (const item of group.conditions) {
    if (isFilterGroup(item)) {
      const nested = serializeDagFilterGroup(item, fieldMeta)
      if (nested.conditions.length > 0) conditions.push(nested)
      continue
    }
    const serialized = serializeCondition(item, fieldMeta)
    if (serialized) conditions.push(serialized)
  }
  return { op: group.op, conditions }
}
